import {RowDragEvent, RowDragMoveEvent, ValueFormatterFunc, ValueGetterParams} from "@ag-grid-community/core";
import {
  isBothStop,
  isCompleted,
  isDeliveryOrBothStop,
  isDeliveryStop,
  isInCompleted,
  isPickupOrBothStop,
  isPickupStop,
  jobStopStatusToString
} from "../../../../utils/stops/StopUtils";

import {AgGridReact} from "@ag-grid-community/react";
import {cloneDeep, isEqual} from "lodash";
import {
  DriverQualification,
  Eta,
  Manifest,
  ManifestStop,
  RouteOptimizationActivity,
  Skill
} from "../../../../generated/graphql";
import {isValidAddress} from "../../../../utils/General";
import {OptimizationLocation} from "../ManifestDetails.type";
import {addMinutes} from "date-fns";
import {getIANATimezone} from "../../../../utils/DateTimeUtils";
import {Constants} from "../../../../constants/constants";
import {getTimezoneOffset} from "date-fns-tz";
import {getStopTimeByStopType} from "../../../../utils/stops/StopTimeUtils";
import log from "loglevel";
import {ETASource} from "../enums/enums";
import {TManifestEnhanced, TManifestStopEnhanced} from "../../../common/types";
import {
  getDate,
  getStopDateCellValue,
  getTime,
  StopDateTimeType
} from "../../../common/cell-renderers/DateTimeCellRenderer";
import {DoubleFieldCellValue} from "../../../common/cell-renderers/DoubleFieldCellRenderer";

let animationFrame: number | undefined = undefined;

type ScrollAction = "start" | "stop";

//Every 100 rows will take 7 seconds to scroll from bottom to top.
export const AverageDuration = 7000;
export const AverageRows = 100;

export const calculateAverageScrollDurationByTotalRows = (totalRows: number) => {
  return (AverageDuration * totalRows) / AverageRows;
};

export const calculateRemainingScrollDuration = (
  scrollTop: number,
  scrollHeight: number,
  averageScrollDuration: number
) => {
  return averageScrollDuration * (scrollTop / scrollHeight);
};

export const handleScrollTop = (type: ScrollAction, totalRows?: number) => {
  if (type === "start" && totalRows) {
    const scrollElements = document.querySelectorAll(".ag-body-vertical-scroll-viewport");
    const averageScrollDurationByTotalRows = calculateAverageScrollDurationByTotalRows(totalRows);
    scrollElements.forEach((element) => {
      const currentScrollTop = element.scrollTop;
      const currentScrollHeight = element.scrollHeight;
      const remainingScrollDuration = calculateRemainingScrollDuration(
        currentScrollTop,
        currentScrollHeight,
        averageScrollDurationByTotalRows
      );
      const startTime = performance.now();

      function animateScroll(timestamp: number) {
        const elapsed = timestamp - startTime;
        const progress = elapsed / remainingScrollDuration;

        const newScrollTop = currentScrollTop - currentScrollTop * progress;

        element.scrollTo({
          top: newScrollTop,
          behavior: "auto"
        });

        if (elapsed >= remainingScrollDuration) {
          animationFrame = undefined;
          return;
        }

        animationFrame = requestAnimationFrame(animateScroll); // Continue scrolling
      }

      // Start the animation
      animationFrame = requestAnimationFrame(animateScroll);
    });
  } else {
    if (animationFrame) {
      cancelAnimationFrame(animationFrame);
      animationFrame = undefined;
    } else {
      return;
    }
  }
};

export const checkRowMoveAndDoFunction = (
  event: RowDragEvent,
  func: (arg: ScrollAction, totalRows?: number) => void,
  totalRows?: number
) => {
  if (event.type === "rowDragMove") {
    func("stop");
  } else if (event.type === "rowDragLeave" && event.vDirection === "up" && totalRows) {
    func("start", totalRows);
  } else {
    return;
  }
};

export const checkNoSpaceBetweenSelectedRows = (rowIndexes: number[]) => {
  for (let index = 0; index < rowIndexes.length - 1; index++) {
    if (rowIndexes[index] + 1 !== rowIndexes[index + 1]) {
      return false;
    }
  }
  return true;
};

export const getDraggingStopsList = (
  event: RowDragMoveEvent,
  workingStops: TManifestStopEnhanced[],
  setFunction: React.Dispatch<React.SetStateAction<TManifestStopEnhanced[]>>
) => {
  const draggingRowsData = event.nodes.map((node) => node.data) as TManifestStopEnhanced[];
  const overRowData = event.overNode?.data as TManifestStopEnhanced;
  if (event.nodes[0].rowIndex === event.overIndex) {
    return;
  }

  const _Items = [...(workingStops ?? [])];

  // Calculate the index where the dragging rows will be inserted
  let insertIndex = _Items.findIndex(
    (stop) => stop?.manifestSequence === overRowData?.manifestSequence && stop.jobStopId === overRowData.jobStopId
  );

  if (insertIndex === -1) {
    insertIndex = _Items.length;
  }

  // Remove dragging rows from their original positions
  draggingRowsData.forEach((draggingRow) => {
    const draggingRowIndex = _Items.findIndex(
      (stop) => stop?.manifestSequence === draggingRow?.manifestSequence && stop.jobStopId === draggingRow.jobStopId
    );
    if (draggingRowIndex !== -1) {
      _Items.splice(draggingRowIndex, 1);
    }
  });

  // Insert dragging rows at the calculated insertIndex
  _Items.splice(insertIndex, 0, ...draggingRowsData);

  setFunction(_Items);
};

export const jobStopStatusValueFormatter: ValueFormatterFunc<{value: string}> = ({value}) =>
  jobStopStatusToString(value);

export const getManifestWrongOrderJobNumbers = (stops: TManifestStopEnhanced[]) => {
  const jobStops: Record<string, TManifestStopEnhanced[]> = {};
  const wrongOrderJobs = new Set<string>();

  for (const stop of stops) {
    const jobNumber = stop.job.jobNumber;
    if (!jobStops[jobNumber]) {
      jobStops[jobNumber] = [];
    }
    jobStops[jobNumber].push(stop);
  }

  for (const jobNumber in jobStops) {
    const jobNumberStops = jobStops[jobNumber];
    const activeStops = jobNumberStops.filter(isInCompleted);
    const isHasActiveDeliveryOrBothStops = activeStops.some(isDeliveryOrBothStop);
    if (!isHasActiveDeliveryOrBothStops) {
      continue;
    }

    activeStops.forEach((item, index) => {
      const previousActiveStops = activeStops.slice(0, index);
      const followingActiveStops = activeStops.slice(index + 1);
      if (index === activeStops.length - 1) {
        if (isPickupStop(item)) {
          wrongOrderJobs.add(jobNumber);
        }
        if (
          isBothStop(item) &&
          previousActiveStops.some(isDeliveryStop) &&
          !followingActiveStops.some(isDeliveryStop)
        ) {
          wrongOrderJobs.add(jobNumber);
        }
      } else if (isDeliveryStop(item) || isBothStop(item)) {
        const isHasSupportedPickupStop = previousActiveStops.some(isPickupOrBothStop);
        if (!isHasSupportedPickupStop && followingActiveStops.some(isPickupStop)) {
          wrongOrderJobs.add(jobNumber);
        }
      }
    });
  }

  return Array.from(wrongOrderJobs);
};

export const isStopsSequenceChanged = (
  initialStops: TManifestStopEnhanced[] | undefined,
  gridRef: React.RefObject<AgGridReact<TManifestStopEnhanced>>
) => {
  if (!gridRef.current || !initialStops) return false;
  const initialJobStopIdsInOrder = initialStops.map((stop) => stop.jobStopId);

  const currentJobStopIdsInOrder: number[] = [];
  gridRef.current.api.forEachNode((row) => {
    currentJobStopIdsInOrder.push(row.data!.jobStopId);
  });

  return !isEqual(initialJobStopIdsInOrder, currentJobStopIdsInOrder);
};

export const getEtaSourcesDirty = (
  initialStops: TManifestStopEnhanced[] | undefined,
  gridRef: React.RefObject<AgGridReact<TManifestStopEnhanced>>
) => {
  const result = new Set<ETASource>();
  if (!gridRef.current || !initialStops) return [];

  const activeStops = initialStops.filter(isInCompleted);
  const generatedETAJobStopIds = activeStops.filter((stop) => !!stop.eta).map((stop) => stop.jobStopId);
  if (generatedETAJobStopIds.length === 0) return [];
  log.debug("generatedETAJobStopIds: ", generatedETAJobStopIds);

  const currentActiveJobStopIds: number[] = [];
  gridRef.current.api.forEachNode((row) => {
    if (isInCompleted(row.data!)) {
      currentActiveJobStopIds.push(row.data!.jobStopId);
    }
  });

  log.debug("currentActiveJobStopIds: ", currentActiveJobStopIds);

  // Check for resequenced stops
  generatedETAJobStopIds.forEach((jobStopId, i) => {
    if (jobStopId !== currentActiveJobStopIds[i]) {
      const stop = activeStops.find((s) => s.jobStopId === jobStopId);
      if (stop?.etaSource) {
        result.add(stop.etaSource as ETASource);
      }
    }
  });

  log.debug("ETA sources dirty: ", Array.from(result));
  return Array.from(result);
};

export const decorateManifestETA = (manifest: Manifest | TManifestEnhanced, eta: Eta | undefined) => {
  log.debug("decorating manifest ETA");
  const clonedManifest = cloneDeep(manifest) as TManifestEnhanced;
  clonedManifest.etaMessage = eta?.message ?? "";
  clonedManifest.latestCompletedJobStopId = eta?.latestCompletedJobStopIds?.[eta?.latestCompletedJobStopIds.length - 1];
  clonedManifest.stops?.forEach((stop: TManifestStopEnhanced) => {
    if (eta?.message === "Success") {
      const jobStopETA = eta?.items.find((item) => item.jobStopId === stop.jobStopId);
      if (jobStopETA) {
        stop.eta = jobStopETA.eta;
        stop.generatedDateTime = jobStopETA.generatedDateTime;
        stop.etaSource = jobStopETA.etaSource;
      }
    }
    const lastSent = eta?.lastSentItems?.find((item) => item.jobStopId === stop.jobStopId);
    if (lastSent) {
      stop.notificationSentData = lastSent;
    }
  });
  return clonedManifest;
};

export const getNotificationWindowCellValue = (
  stop: TManifestStopEnhanced | undefined,
  is24HourFormat: boolean,
  isTimezoneOfJobStop: boolean
): DoubleFieldCellValue | undefined => {
  if (!stop) return undefined;

  const {notificationSentData, timeZone} = stop;
  const windowStart = notificationSentData?.windowStart;
  const windowEnd = notificationSentData?.windowEnd;

  if (windowStart && windowEnd && timeZone) {
    const startTime = getTime(windowStart, timeZone, isTimezoneOfJobStop, is24HourFormat, {includeZone: false});
    const endTime = getTime(windowEnd, timeZone, isTimezoneOfJobStop, is24HourFormat);
    const startDate = getDate(windowStart, timeZone, isTimezoneOfJobStop);
    const endDate = getDate(windowEnd, timeZone, isTimezoneOfJobStop);
    // Pair each time with its own date so a window spanning midnight is unambiguous.
    // Same-day windows show the date once at the end: "23:56 - 00:46 EDT Jul 09".
    const windowDisplay =
      startDate === endDate
        ? `${startTime} - ${endTime} ${endDate}`
        : `${startTime} ${startDate} - ${endTime} ${endDate}`;

    return {
      upperField: {
        value: windowDisplay,
        style: {fontWeight: 400, lineHeight: "16px", color: "#111827"}
      },
      lowerField: {
        value: "",
        style: {}
      }
    };
  }

  // Completed stop with no data (expired or never sent) → empty
  if (isCompleted(stop)) {
    return undefined;
  }

  // Active stop with no data (not yet sent) → N/A
  return {
    upperField: {
      value: "N/A",
      style: {fontWeight: 400, lineHeight: "16px", color: "#6B7280"}
    },
    lowerField: {
      value: "",
      style: {}
    }
  };
};

// CSV export variant of the ETA notification window: date leads each timestamp.
// e.g. "Jul 09 23:56 - Jul 10 00:46 EDT" (cross-midnight), "Jun 15 2:00 PM - 3:00 PM EDT" (same day).
export const getNotificationWindowExportValue = (
  stop: TManifestStopEnhanced | undefined,
  is24HourFormat: boolean,
  isTimezoneOfJobStop: boolean
): string => {
  if (!stop) return "";

  const {notificationSentData, timeZone} = stop;
  const windowStart = notificationSentData?.windowStart;
  const windowEnd = notificationSentData?.windowEnd;

  if (windowStart && windowEnd && timeZone) {
    const startTime = getTime(windowStart, timeZone, isTimezoneOfJobStop, is24HourFormat, {includeZone: false});
    const endTime = getTime(windowEnd, timeZone, isTimezoneOfJobStop, is24HourFormat);
    const startDate = getDate(windowStart, timeZone, isTimezoneOfJobStop);
    const endDate = getDate(windowEnd, timeZone, isTimezoneOfJobStop);

    return startDate === endDate
      ? `${startDate} ${startTime} - ${endTime}`
      : `${startDate} ${startTime} - ${endDate} ${endTime}`;
  }

  // Completed stop with no data → empty; active stop with no data → N/A (mirrors the cell value).
  return isCompleted(stop) ? "" : "N/A";
};

export const getTopmostStop = (manifest: Manifest | TManifestEnhanced): TManifestStopEnhanced | null => {
  if (!manifest.stops?.length) return null;

  const incompleteStops = manifest.stops.filter(isInCompleted);
  if (!incompleteStops.length) return null;

  let topmostStop: TManifestStopEnhanced | null = null;
  let minSequence = Number.MAX_SAFE_INTEGER;
  for (const stop of incompleteStops) {
    const seq = stop.manifestSequence ?? Number.MAX_SAFE_INTEGER;
    if (seq < minSequence) {
      minSequence = seq;
      topmostStop = stop as TManifestStopEnhanced;
    }
  }
  return topmostStop;
};

export const optimizeManifest = (manifest: TManifestEnhanced) => {
  log.debug("Optimizing %s", manifest.manifestDriverId);
};

export const findNotCompletedStops = (manifestStops: TManifestStopEnhanced[] | undefined | null) => {
  if (!manifestStops) {
    return [];
  }
  return manifestStops.filter(isInCompleted);
};

export const findEarliestStopDateV2 = (
  manifestStops: TManifestStopEnhanced[] | undefined | null,
  options: {timezoneOfStartLocation?: string}
): Date | null => {
  const notCompletedStops = findNotCompletedStops(manifestStops);

  if (notCompletedStops.length === 0) return null;

  const equalCurrentDateStops = notCompletedStops
    .filter((stop) => isSameDayMonthYearWithCurrentDate(getStopTimeByStopType(stop, stop.stopType)))
    .map((stop) => ({
      stopDateTime: getStopTimeByStopType(stop, stop.stopType)!,
      timeZone: stop.timeZone!
    }))
    .sort((a, b) => new Date(a.stopDateTime).getTime() - new Date(b.stopDateTime).getTime());

  if (equalCurrentDateStops.length === 0) {
    return null;
  }

  const {timezoneOfStartLocation} = options;

  const targetTimeZone = timezoneOfStartLocation || equalCurrentDateStops[0].timeZone;
  return convertTimeZoneByOffSetInCurrentTime(targetTimeZone);
};

export const isSameDayMonthYearWithCurrentDate = (isoString: string | undefined): boolean => {
  if (!isoString) return false;
  const comparedDate = new Date(isoString);
  const currentDate = new Date();

  return (
    comparedDate.getFullYear() === currentDate.getFullYear() &&
    comparedDate.getMonth() === currentDate.getMonth() &&
    comparedDate.getDate() === currentDate.getDate()
  );
};

export const convertTimeZoneByOffSetInCurrentTime = (timeZone: string) => {
  const offsetBetweenTimeZoneAndUTC = getTimezoneOffset(getIANATimezone(timeZone)) / 1000 / 60;
  const offsetBetweenBrowserTimeZoneAndUTC = Constants.BROWSER_TIMEZONE_OFFSET_MINUTES;
  const diffBetweenTimeZoneAndBrowser = offsetBetweenTimeZoneAndUTC - offsetBetweenBrowserTimeZoneAndUTC;
  return addMinutes(new Date(), diffBetweenTimeZoneAndBrowser);
};

export enum GRAPHHOPPER_INVALID_MSG {
  ADDRESS = "Route optimization request contains invalid addresses."
}

export const filterInvalidStartEndLocation = async (startingLocation: string, endLocation: string | undefined) => {
  if (!startingLocation && !endLocation) return [];
  const results: OptimizationLocation[] = [];

  const isValidStartLocation = await isValidAddress(startingLocation);
  if (!isValidStartLocation) {
    results.push({
      nameOfLocation: "Starting Location",
      address: startingLocation
    });
  }
  if (endLocation?.trim()) {
    const isValidEndLocation = await isValidAddress(endLocation);
    if (!isValidEndLocation) {
      results.push({
        nameOfLocation: "End Location",
        address: endLocation
      });
    }
  }
  return results;
};

export const filterInvalidAddresses = async (arrayOfAddress: any): Promise<OptimizationLocation[]> => {
  if (!arrayOfAddress || arrayOfAddress.length === 0) return [];

  // Build unique addresses map and track which stops use each address
  const addressToStopsMap = new Map<string, OptimizationLocation[]>();
  for (const {manifestSequence, address, city, state, zip} of arrayOfAddress) {
    const addressOfStop = `${address}, ${city}, ${state} ${zip}`;
    if (!addressToStopsMap.has(addressOfStop)) {
      addressToStopsMap.set(addressOfStop, []);
    }
    addressToStopsMap.get(addressOfStop)!.push({manifestSequence, address: addressOfStop});
  }

  const uniqueAddresses = Array.from(addressToStopsMap.keys());
  const invalidAddressSet = new Set<string>();

  // Process addresses in batches of 10
  const BATCH_SIZE = 10;
  for (let i = 0; i < uniqueAddresses.length; i += BATCH_SIZE) {
    const batch = uniqueAddresses.slice(i, i + BATCH_SIZE);
    const validationResults = await Promise.all(batch.map((addr) => isValidAddress(addr)));

    batch.forEach((addr, index) => {
      if (!validationResults[index]) {
        invalidAddressSet.add(addr);
      }
    });
  }

  // Collect all stops with invalid addresses and sort by manifestSequence
  const invalidAddresses: OptimizationLocation[] = [];
  for (const invalidAddr of invalidAddressSet) {
    const stops = addressToStopsMap.get(invalidAddr) ?? [];
    invalidAddresses.push(...stops);
  }

  return invalidAddresses.sort((a, b) => (a.manifestSequence ?? 0) - (b.manifestSequence ?? 0));
};

export const extractJobStopIdFromActivity = (activitie: RouteOptimizationActivity) => {
  let jobStopId: number | null = null;
  if (activitie.type === "pickupShipment") {
    jobStopId = parseInt(activitie.id?.split(":")[1] ?? "0");
  } else if (activitie.type === "deliverShipment") {
    jobStopId = parseInt(activitie.id?.split(":")[2] ?? "0");
  }
  return jobStopId;
};

export const DateValueGetter = (
  params: ValueGetterParams<TManifestStopEnhanced>,
  typeOfDateTime: StopDateTimeType,
  isTimezoneOfJobStop: boolean
) => {
  return getStopDateCellValue({
    data: params.data as ManifestStop,
    typeOfDateTime: typeOfDateTime,
    isTimezoneOfJobStop: isTimezoneOfJobStop
  });
};

export const isHideETA = (params: ValueGetterParams<TManifestStopEnhanced>, etaSourcesDirty: ETASource[]) => {
  return (
    etaSourcesDirty.includes(ETASource.COMPLETE_STOP) ||
    etaSourcesDirty.includes(ETASource.GPS_UPDATE) ||
    etaSourcesDirty.includes(params.data?.etaSource as ETASource)
  );
};

export const getActiveDriverSkills = (driverSkills: Skill[], availableSkills: Skill[]): DriverQualification[] => {
  const activeSkillIds = (driverSkills ?? []).map((el) => el.id);
  return (availableSkills ?? [])
    .filter((el) => activeSkillIds.includes(el.id))
    .map((el) => ({
      id: el.id,
      code: el.name.slice(0, 3).toUpperCase(),
      name: el.name
    })) as unknown as DriverQualification[];
};

/**
 * DP-3214/DP-3216: stands in for "no colour", i.e. least important of all. With the ascending
 * order being least-important-first, that puts an unstamped stop at the TOP of an ascending sort.
 * Larger than any rank the settings UI can produce, and kept well off Number.MAX_SAFE_INTEGER so
 * the subtraction in the comparator below cannot overflow into a wrong sign.
 */
export const UNSTAMPED_INDICATOR_RANK = 1_000_000;

/**
 * DP-3214: how the Manifest Details colour column orders two stops.
 *
 * With backend colours the worker has stamped the authoritative rank on each stop, so order by
 * that. The client fallback can only match a rule by COLOUR, so where a tenant reuses one colour
 * across two ranks it cannot tell them apart and returns whichever rule it finds first — while
 * the server knows the real rank per row. Nothing enforces that colours are unique per rank.
 *
 * DP-3216: ascending is LEAST-important-first, so the rank branch subtracts the other way round.
 * Rank 1 is the most important colour, and the client path below — which sorts on a priority the
 * rule provider derives by inverting rank — has always put the least important at the top of an
 * ascending sort. Sorting by raw rank ascending reversed that the moment the flag went on.
 *
 * Unstamped stops therefore lead an ascending sort, matching the client, which scores them 0.
 *
 * `getPriority` is injected rather than imported so this stays a leaf module — `getColorPriority`
 * lives in JobPanel, and importing a screen-sized component into a util would be a needless
 * dependency (and is only ever needed on the client path).
 */
export const compareIndicatorColumn = (
  {
    backendColorRulesEnabled,
    getPriority
  }: {backendColorRulesEnabled?: boolean; getPriority: (color?: string | null) => number},
  a: {color?: string | null; rank?: number | null},
  b: {color?: string | null; rank?: number | null}
): number =>
  backendColorRulesEnabled
    ? (b.rank ?? UNSTAMPED_INDICATOR_RANK) - (a.rank ?? UNSTAMPED_INDICATOR_RANK)
    : getPriority(a.color) - getPriority(b.color);
